import { useCallback, useEffect, useMemo, useState } from "react";
import { getPatientUiConfig } from "../../services/patientService";
import { getSessionTenantId } from "../../utils/tenantContext";
import {
  DEFAULT_ENABLED_TAB_KEYS,
  isPatientTabKey,
  pickDefaultTab,
} from "./patientTabs";

const STORAGE_KEY_PREFIX = "hrms_patient_ui_tabs";

function storageKey() {
  const tenantId = getSessionTenantId();
  return tenantId ? `${STORAGE_KEY_PREFIX}_${tenantId}` : STORAGE_KEY_PREFIX;
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(storageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.enabledTabKeys)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    sessionStorage.setItem(storageKey(), JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

export function clearPatientTabsCache() {
  try {
    const key = storageKey();
    sessionStorage.removeItem(key);
    // Legacy unscoped key from older builds
    sessionStorage.removeItem(STORAGE_KEY_PREFIX);
  } catch {
    // ignore
  }
  inflight = null;
}

let inflight = null;

function cacheIsStale(cached) {
  if (!cached?.enabledTabKeys?.length) return true;
  const have = new Set(cached.enabledTabKeys);
  // Refetch when the catalog gains new tab keys (e.g. medication split).
  return DEFAULT_ENABLED_TAB_KEYS.some((key) => !have.has(key));
}

/**
 * Load tenant Patient chart tab config (enabled keys).
 * Falls back to all tabs enabled if the request fails so the module stays usable.
 */
export async function loadPatientUiConfig({ force = false } = {}) {
  if (!force) {
    const cached = readCache();
    if (cached && !cacheIsStale(cached)) return cached;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await getPatientUiConfig();
      const payload = res?.result || res?.data || res || {};
      const keys = (payload.enabledTabKeys || [])
        .filter(isPatientTabKey);
      const normalized = {
        tabs: Array.isArray(payload.tabs) ? payload.tabs : [],
        enabledTabKeys: keys.length ? keys : DEFAULT_ENABLED_TAB_KEYS,
      };
      writeCache(normalized);
      return normalized;
    } catch {
      const fallback = {
        tabs: [],
        enabledTabKeys: DEFAULT_ENABLED_TAB_KEYS,
      };
      return fallback;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Hook for Patient chart surfaces that need tenant tab visibility.
 */
export default function usePatientTabs() {
  const sessionTenantId = getSessionTenantId();
  const [enabledTabKeys, setEnabledTabKeys] = useState(() => {
    const cached = readCache();
    return cached?.enabledTabKeys || DEFAULT_ENABLED_TAB_KEYS;
  });
  const [loading, setLoading] = useState(() => !readCache());
  const [error, setError] = useState(null);

  const refresh = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const config = await loadPatientUiConfig(opts);
      setEnabledTabKeys(config.enabledTabKeys);
      return config;
    } catch (err) {
      setError(err);
      setEnabledTabKeys(DEFAULT_ENABLED_TAB_KEYS);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const config = await loadPatientUiConfig({ force: true });
      if (cancelled) return;
      setEnabledTabKeys(config.enabledTabKeys);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionTenantId]);

  const enabledSet = useMemo(
    () => new Set(enabledTabKeys),
    [enabledTabKeys],
  );

  const isTabEnabled = useCallback(
    (tabKey) => enabledSet.has(tabKey),
    [enabledSet],
  );

  const defaultTab = useMemo(
    () => pickDefaultTab(enabledTabKeys),
    [enabledTabKeys],
  );

  return {
    enabledTabKeys,
    enabledSet,
    isTabEnabled,
    defaultTab,
    loading,
    error,
    refresh,
  };
}
